<template>
  <ShortcutWrapper :nShortcutRequiredClaim="UserClaims.ContractManager" @n-shortcut="contractMode.value !== 0 ? parseTableAction({ actionType: 'create-contract' }) : ''">
    <BPCard
      :contractMode.sync="contractMode"
      :headerConfig="headerConfig"
      id="contract-table">
      <ContractTable
        :contracts="allContracts"
        :isTractDetail="false"
        :contractMode="contractMode"
        @refresh-contracts="refreshContracts"
        @contract-action="parseTableAction"
      />
      <Dialog :stateId="dialogId"
        :enforcePersistence="requireEditApproval"
      >
        <ContractCreator
          :contractMode="contractMode"
          v-if="creating"
          @contract-changed="save"
        />
        <EditContract
          v-if="editing"
          :contractId="focusedContract.contractId"
          :contractMode="contractMode"
          :shouldAddActivity="shouldAddActivity"
          :editProducts="editProducts"
          :companyRequiresEditApproval="companyInfo.requireApprovalForContractModifications"
          :currentDraftMode="focusedContract.isDraft"
          @contract-changed="save"
          @contract-changed-keep-open="refreshContracts"
          @close="closeEditor"
        />
        <AddWithBundleDialog
          v-if="bundleDialog"
          :contractId="focusedContract.contractId"
          :bundle="focusedBundle"
          :contractMode="contractMode"
          @added-activities-close="closeBundleDialog(true)"
          @close="closeBundleDialog(false)"
        />
        <ContractDetail
          @edit-activities="editActivities"
          @edit-contract="editContract"
          @edit-products="editContractProducts"
          v-if="contractDetail"
          :contractId="focusedContract.contractId"
          :hasPendingTickets="focusedContract ? focusedContract.hasPendingTickets : false"
          @review="refreshContracts"
        />
        <BaseRateContracts
          v-if="baseRateFinder"
          :contractMode="contractMode"
          :includeProduction="!!filter.includeProduction"
          :includeWoodsSale="!!filter.includeWoodsSale"
          :includeTransfer="!!filter.includeTransfer"
          :includeLogYardSale="!!filter.includeLogYardSale"
        />
        <RateSheetDivergence
          v-if="rateSheetDivergence"
          :contractMode="contractMode"
          :includeProduction="!!filter.includeProduction"
          :includeWoodsSale="!!filter.includeWoodsSale"
          :includeTransfer="!!filter.includeTransfer"
          :includeLogYardSale="!!filter.includeLogYardSale"
        />
        <ScaleSampleFrequencies
          v-if="scaleSampleFrequencies"
          :contractMode="contractMode"
        />
        <TractDetail
          :tractId="focusedTractId"
          v-if="focusedTractId != -1"
          @edit-tract="editTract"
          @view-tract="focusedTractId = $event.tractId"
        />
        <ImportContractTickets
          v-if="importingByproductTickets"
          :contractMode="contractMode"
          :isUpdate="isTicketUpdate"
          @tickets-uploaded="refreshContracts"
          @close="resetDialogs"
        />
        <ConfirmDelete
          v-if="deleting"
          title="Contract"
          @delete="deleteItem"
          @cancel-delete="resetDialogs"/>
      </Dialog>
    </BPCard>
  </ShortcutWrapper>
</template>

<script>
import { mapMutations, mapActions, mapGetters } from 'vuex'
import { LocalStorageKeys } from '@/utils/LocalStorageActor'
import { ContractMode, UserClaims } from '@/utils/Enumerations.js'
import { SETTING_KEYS, SETTING_TYPES } from '@/utils/UserSettings'
import RouterJump from '@/model/RouterJump.js'
import { contractModeFor, uniqueDialogId } from '@/utils/componentHelpers'
export default {
  name: 'Contracts',

  components: {
    BPCard: () => import('@/components/core/BPCard.vue'),
    ContractDetail: () => import('@/components/contract/contract-detail/ContractDetail.vue'),
    ContractCreator: () => import('@/components/contract/ContractCreator.vue'),
    EditContract: () => import('@/components/contract/ContractEditor.vue'),
    ConfirmDelete: () => import('@/components/helper/ConfirmDelete.vue'),
    ContractTable: () => import('@/components/contract/ContractTable.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    TractDetail: () => import('@/components/tract/tract-detail/TractDetail.vue'),
    AddWithBundleDialog: () => import('@/components/contract/AddWithBundleDialog.vue'),
    ShortcutWrapper: () => import('@/components/core/ShortcutWrapper.vue'),
    ImportContractTickets: () => import('@/components/contract/import-tickets/ImportContractTickets.vue'),
    BaseRateContracts: () => import('@/components/contract/base-rate-finder/BaseRateContracts.vue'),
    RateSheetDivergence: () => import('@/components/contract/rate-sheet-divergence/RateSheetDivergence.vue'),
    ScaleSampleFrequencies: () => import('@/components/contract/scale-sample-frequencies/ScaleSampleFrequencies.vue')
  },

  data: () => ({
    dialogId: uniqueDialogId('contracts'),
    deleting: false,
    contractDetail: false,
    creating: false,
    editing: false,
    shouldAddActivity: false,
    editProducts: false,
    isTicketUpdate: false,
    focusedTractId: -1,
    focusedContract: {},
    contractMode: null,
    focuseBundle: undefined,
    bundleDialog: false,
    importingByproductTickets: false,
    baseRateFinder: false,
    rateSheetDivergence: false,
    scaleSampleFrequencies: false,
    loading: null,
    UserClaims
  }),

  async created () {
    this.loading = true

    if (!this.routerJump) {
      this.initializeFilter()

      const contractMode = localStorage.getItem(LocalStorageKeys.CONTRACT_GRID_TICKET_MODE)

      try {
        this.contractMode = ContractMode.forInt(JSON.parse(contractMode).value) ?? ContractMode.Logs
      } catch {
        this.contractMode = ContractMode.Logs
      }
    }
  },

  async mounted () {
    this.resetDialogs()
    await this.handleRouterJump()
    this.loading = false
  },

  beforeDestroy () {
    localStorage.setItem(LocalStorageKeys.CONTRACT_GRID_TICKET_MODE, JSON.stringify(this.contractMode))
  },

  watch: {
    contractMode (val) {
      localStorage.setItem(LocalStorageKeys.CONTRACT_GRID_TICKET_MODE, JSON.stringify(this.contractMode))
      this.setFilterForContractMode(val)
    },

    routerJump: {
      handler () {
        if (this.loading === false) {
          this.handleRouterJump()
        }
      },
      immediate: true
    }
  },

  computed: {
    ...mapGetters('contract', ['allContracts', 'filter']),
    ...mapGetters('user-settings', ['mutatedUserSettings']),
    ...mapGetters('global', ['routerJump']),
    ...mapGetters('user', ['companyInfo']),
    headerConfig () {
      switch (this.contractMode?.value) {
        case ContractMode.Logs.value: return { title: this.$t('logsContracts') }
        case ContractMode.Byproducts.value: return { title: this.$t('byproductContracts') }
        case ContractMode.Transfers.value: return { title: this.$t('transferContracts') }
        case ContractMode.LogYardSale.value: return { title: this.$t('logYardSaleContracts') }
      }
      return null
    },

    requireEditApproval () {
      return this.companyInfo.requireApprovalForContractModifications && !this.focusedContract.isDraft && this.editing
    }
  },

  methods: {
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapMutations('global', ['setRouterJump']),
    ...mapActions('contract', ['fetchContracts', 'deleteContract', 'initializeFilter', 'setFilter', 'getContract']),
    async handleRouterJump () {
      if (this.routerJump) {
        let { contract, editActivities, editProducts, contractId } = this.routerJump.sourceObject
        if (!contract && contractId !== undefined) {
          contract = await this.getContract(contractId)
        }

        switch (this.routerJump.sourceView) {
          case 'Tickets':
            this.shouldAddActivity = editActivities
            this.editProducts = editProducts
            this.focusedContract = contract
            this.editing = true
            this.openOrUpdateDialog({ id: this.dialogId, width: '70%' })
            break
          case 'Toolbar':
            this.parseTableAction({
              actionType: 'contract-detail',
              contract: await this.getContract(this.routerJump.sourceObject.contractId)
            })
            break
        }
        this.contractMode = contractModeFor(contract.type)
      }
      this.setRouterJump(undefined)
    },

    editTract () {
      const routerJump = new RouterJump('Tickets', 'Tracts', {
        tractId: this.focusedTractId
      })
      this.setRouterJump(routerJump)
      this.$router.push('tracts')
    },

    parseTableAction (action) {
      this.resetDialogs()

      if (action.contract) {
        this.focusedContract = action.contract
      }

      switch (action.actionType) {
        case 'import-tickets':
          this.importingByproductTickets = true
          this.isTicketUpdate = action.isUpdate
          this.openOrUpdateDialog({ id: this.dialogId, width: '50vw' })
          break
        case 'edit-contract':
          this.editing = true
          this.editProducts = false
          this.shouldAddActivity = false
          this.openOrUpdateDialog({ id: this.dialogId, width: '80vw' })
          break
        case 'view-tract':
          this.focusedTractId = this.focusedContract.tractId
          this.openOrUpdateDialog({ id: this.dialogId, width: '80vw' })
          break
        case 'contract-detail':
          this.contractDetail = true
          this.openOrUpdateDialog({ id: this.dialogId, width: '80vw' })
          break
        case 'create-contract':
          this.creating = true
          this.openOrUpdateDialog({ id: this.dialogId, width: '80vw' })
          break
        case 'delete-contract':
          this.deleting = true
          this.openOrUpdateDialog({ id: this.dialogId, width: '400px', allowFullscreen: false })
          break
        case 'rate-sheet-divergence':
          this.rateSheetDivergence = true
          this.openOrUpdateDialog({ id: this.dialogId, width: '85vw' })
          break
        case 'base-rate-finder':
          this.baseRateFinder = true
          this.openOrUpdateDialog({ id: this.dialogId, width: '85vw' })
          break
        case 'scale-sample-frequencies':
          this.scaleSampleFrequencies = true
          this.openOrUpdateDialog({ id: this.dialogId, width: '900px' })
      }
    },

    async refreshContracts () {
      await this.fetchContracts(this.filter)
    },

    closeEditor (event) {
      this.resetDialogs()
      if (!event?.refresh) return
      this.refreshContracts()
    },

    editContract (contract) {
      this.resetDialogs()
      this.focusedContract = Object.assign({}, contract)
      this.editing = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '80vw' })
    },

    editActivities (contract) {
      this.resetDialogs()
      this.openOrUpdateDialog({ id: this.dialogId, width: '80vw' })
      this.focusedContract = Object.assign({}, contract)
      this.shouldAddActivity = true
      this.editProducts = false
      this.editing = true
    },

    editContractProducts (contract) {
      this.resetDialogs()
      this.openOrUpdateDialog({ id: this.dialogId, width: '80vw' })
      this.focusedContract = Object.assign({}, contract)
      this.editProducts = true
      this.shouldAddActivity = false
      this.editing = true
    },

    openBundleDialog ({ contract, selectedBundle }) {
      this.resetDialogs()
      this.focusedContract = Object.assign({}, contract)
      this.focusedBundle = Object.assign({}, selectedBundle)
      this.openOrUpdateDialog({ id: this.dialogId, width: '80vw' })
      this.bundleDialog = true
    },

    closeBundleDialog (activitiesCreated) {
      if (activitiesCreated) {
        this.closeDialogsAtOrAbove(this.dialogId)
        this.shouldAddActivity = false
        this.editProducts = false
        this.bundleDialog = false
        this.refreshContracts()
        this.editActivities({ ...this.focusedContract })
      } else {
        this.resetDialogs()
      }
    },

    async deleteItem () {
      await this.deleteContract(this.focusedContract.contractId)
      this.resetDialogs()
      await this.refreshContracts()
    },

    resetDialogs () {
      this.closeDialogsAtOrAbove(this.dialogId)
      this.dialog = false
      this.editing = false
      this.deleting = false
      this.creating = false
      this.contractDetail = false
      this.shouldAddActivity = false
      this.bundleDialog = false
      this.importingByproductTickets = false
      this.baseRateFinder = false
      this.rateSheetDivergence = false
      this.scaleSampleFrequencies = false
      this.focusedTractId = -1
      this.focusedContract = {}
      this.focusedBundle = undefined
    },

    async save (createdContract) {
      this.resetDialogs()
      if (createdContract?.shouldAddActivity) {
        this.$nextTick(_ => {
          if (createdContract.selectedBundle) {
            this.openBundleDialog(createdContract)
          } else {
            this.editActivities(createdContract.contract)
          }
        })
        setTimeout(() => {
          this.shouldAddActivity = false
          this.editProducts = false
        }, 3000)
      }
      await this.refreshContracts()
    },

    userSettingFilterParams (userSettings) {
      if (this.contractMode.value !== 0) return {}

      const ts = new Set(userSettings[SETTING_TYPES.TABLE][SETTING_KEYS.CONTRACT_FILTER_TYPES])
      const typeParams = {
        includeProduction: ts.has(0),
        includeWoodsSale: ts.has(1)
      }

      return typeParams.includeProduction === false && typeParams.includeWoodsSale === false
        ? { includeProduction: true, includeWoodsSale: true }
        : typeParams
    },

    setFilterForContractMode (val) {
      const filter = { ...this.filter }
      filter.includeProduction = false
      filter.includeByProduct = false
      filter.includeByproductPurchase = false
      filter.includeTransfer = false
      filter.includeLogYardSale = false
      filter.includeWoodsSale = false
      filter.includeByproductHauling = false
      switch (val.value) {
        case ContractMode.Logs.value:
          filter.includeProduction = this.userSettingFilterParams(this.mutatedUserSettings).includeProduction ?? true
          filter.includeWoodsSale = this.userSettingFilterParams(this.mutatedUserSettings).includeWoodsSale ?? true
          if (!filter.includeProduction && !filter.includeWoodsSale) filter.includeProduction = true
          break
        case ContractMode.Byproducts.value:
          filter.includeByProduct = true
          filter.includeByproductPurchase = true
          filter.includeByproductHauling = true
          break
        case ContractMode.Transfers.value:
          filter.includeTransfer = true
          break
        case ContractMode.LogYardSale.value:
          filter.includeLogYardSale = true
          break
      }
      this.setFilter(filter)
    }
  }
}
</script>
